import { CheckCircle2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const STATUS_OPTIONS = [
  { label: '出願済', value: 'SUBMITTED' },
  { label: '書類確認中', value: 'CONFIRMED' },
  { label: '入金待ち', value: 'PAYMENT_PENDING' },
  { label: '入金済', value: 'PAID' },
  { label: '合格', value: 'PASSED' },
  { label: '不合格', value: 'FAILED' },
];

interface Application {
  id: string;
  receiptNumber: string;
  status: string;
  updatedAt: string;
  applicant?: { nameFirst: string; nameLast: string };
  offering?: { offering?: { name: string } };
}

export default function ApplicationStatusForm() {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [targetStatus, setTargetStatus] = useState<string>('');

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/applications');
      if (res.ok) {
        setApplications((await res.json()) as Application[]);
      }
    } catch (error) {
      console.error('Failed to fetch applications', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(applications.map((a) => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleBulkUpdate = async () => {
    if (!targetStatus || selectedIds.length === 0) return;

    if (
      !confirm(
        `${selectedIds.length}件の申込を「${STATUS_OPTIONS.find((s) => s.value === targetStatus)?.label}」に変更しますか？`,
      )
    ) {
      return;
    }

    try {
      setLoading(true);
      // In a real app, this would be a bulk UPDATE API
      const res = await fetch('/api/applications/status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ids: selectedIds,
          status: targetStatus,
        }),
      });

      if (res.ok) {
        alert('ステータスを更新しました。');
        setSelectedIds([]);
        fetchApplications();
      }
    } catch (error) {
      console.error('Failed to update status', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">ステータス一括管理</h1>
        <p className="text-gray-500 mt-1">複数の志願者のステータス（合格・不合格・確認済等）をまとめて更新します。</p>
      </div>

      {selectedIds.length > 0 && (
        <Alert className="bg-blue-50 border-blue-200">
          <CheckCircle2 className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">一括操作モード</AlertTitle>
          <AlertDescription className="text-blue-700 flex items-center justify-between">
            <span>{selectedIds.length} 件のデータが選択されています。</span>
            <div className="flex gap-4 items-center">
              <Select onValueChange={setTargetStatus} value={targetStatus}>
                <SelectTrigger className="w-[180px] bg-white">
                  <SelectValue placeholder="新ステータスを選択" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleBulkUpdate} className="bg-blue-600 hover:bg-blue-700">
                実行する
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedIds.length === applications.length && applications.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>受付番号</TableHead>
                <TableHead>氏名</TableHead>
                <TableHead>入試日程</TableHead>
                <TableHead>現在のステータス</TableHead>
                <TableHead>最終更新</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    読み込み中...
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((app) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <Checkbox checked={selectedIds.includes(app.id)} onCheckedChange={() => toggleSelect(app.id)} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{app.receiptNumber}</TableCell>
                    <TableCell>
                      {app.applicant?.nameLast} {app.applicant?.nameFirst}
                    </TableCell>
                    <TableCell>{app.offering?.offering?.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{app.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {new Date(app.updatedAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
